import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import Reveal from "@/components/feature/Reveal";
import { brandValues, siteMeta, siteStats, testimonials } from "@/mocks/site";

const origin = "https://youhuochigua.example";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 诱惑吃瓜",
    url: `${origin}/about`,
    inLanguage: "zh-CN",
    description:
      "了解诱惑吃瓜的品牌定位、发展理念、内容原则与联系方式。我们专注用时间线与证据链梳理娱乐事件的来龙去脉。",
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteMeta.name,
    url: origin,
    email: siteMeta.email,
    description: siteMeta.description,
    slogan: siteMeta.slogan,
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="关于我们 - 诱惑吃瓜 用时间线复盘每个事件"
        description="了解诱惑吃瓜的品牌定位、发展理念、内容原则与联系方式。我们坚持先排时间线、再下结论，所有材料标注来源类型，结论可回溯、可修订。"
        keywords="诱惑吃瓜,关于我们,内容原则,事件复盘,辟谣核实"
        path="/about"
        jsonLd={jsonLd}
      />
      <SiteHeader />
      <main className="bg-background-100">
        <header className="border-b border-background-200 bg-background-50 px-4 pb-10 pt-28 md:px-8 md:pb-14 md:pt-32 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              关于诱惑吃瓜
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {siteMeta.name}是一个专注事件复盘的编辑型内容站点。我们不生产爆料，只做一件事：把已经散落在各处的公开信息重新排好顺序，让读者能自己看清来龙去脉。
            </p>
          </div>
        </header>

        <section className="px-4 py-14 md:px-8 md:py-20 lg:px-12">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="h-72 w-full overflow-hidden rounded-lg bg-background-200 md:h-[26rem]">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20close-up%20of%20stacked%20notebooks%2C%20a%20magnifying%20glass%20and%20printed%20documents%20on%20a%20cream%20wooden%20desk%2C%20soft%20natural%20light%2C%20calm%20analytical%20atmosphere%2C%20muted%20amber%20and%20deep%20green%20accents%2C%20cinematic%20still%20life%20photography%2C%20shallow%20depth%20of%20field%2C%20high%20detail%2C%20no%20people&width=1100&height=1300&seq=guada-about-01&orientation=portrait"
                  alt="整理事件材料、笔记与文档的工作桌面"
                  title="诱惑吃瓜 关于我们"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="lg:col-span-7">
              <SectionHeading
                eyebrow="我们是谁"
                title="一群愿意慢慢把事情讲清楚的人"
                desc="这个站点没有爆料团队，也没有内部消息渠道。我们更擅长的是整理：把公开回应、节目正片、平台数据、通告文件这些材料摊开，按时间排好，再把分歧点单独标出来。"
              />
              <div className="mt-6 space-y-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                <p>
                  之所以做这件事，是因为信息在传播过程中会不断被压缩。一句话被截取、一个片段被单独放出来，语境就会改变。等完整材料出现时，很多人已经形成了固定印象。
                </p>
                <p>
                  所以我们给每篇复盘都配了时间线和证据清单。时间线负责顺序，证据清单负责说清楚每条结论是从哪份材料推出来的。读者如果不同意我们的判断，可以顺着材料自己重新看一遍。
                </p>
                <p>
                  我们也会犯错。当新证据出现并推翻旧结论时，会保留原文并标注修订时间与原因——比起装作一直正确，我们更在意判断过程是否透明。
                </p>
              </div>

              <dl className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                {siteStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-lg border border-background-200 bg-background-50 p-4 text-center"
                  >
                    <dd className="font-heading text-xl font-bold text-primary-600 md:text-2xl">
                      {stat.value}
                    </dd>
                    <dt className="mt-1 text-xs text-foreground-500">{stat.label}</dt>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-50 px-4 py-14 md:px-8 md:py-20 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              eyebrow="内容原则"
              title="四条我们一直守着的底线"
              desc="这些规则决定了什么内容会被收录，也决定了结论会长成什么样子。"
            />
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {brandValues.map((item, index) => (
                <Reveal key={item.title} delay={index * 60}>
                  <div className="flex h-full gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                    <span
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-xl ${
                        index % 2 === 0
                          ? "bg-primary-100 text-primary-700"
                          : "bg-secondary-100 text-secondary-700"
                      }`}
                    >
                      <i className={item.icon} aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-foreground-950">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 md:px-8 md:py-20 lg:px-12">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              eyebrow="读者怎么说"
              title="来自长期读者的反馈"
              desc="这些评价来自持续阅读本站的读者，内容保持原意呈现。"
            />
            <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
              {testimonials.map((item, index) => (
                <Reveal key={item.name} delay={index * 60}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                    <span className="flex h-8 w-8 items-center justify-center text-2xl text-accent-500">
                      <i className="ri-double-quotes-l" aria-hidden="true"></i>
                    </span>
                    <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                      {item.text}
                    </blockquote>
                    <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                        {item.name.slice(0, 1)}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900">
                          {item.name}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {item.role}
                        </span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-background-200 bg-secondary-900 px-4 py-12 md:px-8 md:py-16 lg:px-12">
          <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
                想聊聊，或者想提供线索？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-200">
                邮件：{siteMeta.email}　·　也可以通过留言表单把材料来源发给我们
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
            >
              前往联系我们
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}